import type { RoomsParticipantDistribution } from '@/domain/shared/types'
import { useIntl } from '@/intl'
import { PriceDetails } from '@/scopes/common/components/PriceDetails'
import { PriceDiscounts } from '@/scopes/common/components/PriceInfo'
import { space, tok } from '@sunwebgroup/ui-components'
import { Tag, Typography } from '@sunwebgroup/ui-components/uds'
import { styled } from 'styled-components'

interface PriceProps {
  averagePrice: number
  discounts?: PriceDiscounts
  accommodationId: number
  departureDateRaw?: string
  durationRaw?: number
  mealPlan?: string
  transportType?: string
  participants?: RoomsParticipantDistribution
  className?: string
}

const PriceWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`
const PriceHeader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${space('primitives_space_8')};
  align-self: stretch;

  ${Typography} {
    color: ${tok('semantics_content_secondary_color')};
  }

  ${Tag} {
    background-color: ${tok('primitives_color_neutral_150')};
    ${Typography} {
      color: ${tok('primitives_color_neutral_1000')};
    }
  }
`
const PriceSupportingText = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${space('primitives_space_2')};
`

function PriceComponent({ className, averagePrice, discounts, accommodationId, departureDateRaw, durationRaw, mealPlan, participants, transportType }: PriceProps) {
  const intl = useIntl()
  const formattedPrice = intl.formatTruncatedCurrency(averagePrice)
  const formattedPriceDiscounts = discounts
    ? {
        discountPercentage: discounts.discountPercentage,
        originalPrice: intl.formatCurrency(discounts.originalPrice),
      }
    : undefined
  const hasDiscount = formattedPriceDiscounts && formattedPriceDiscounts.discountPercentage > 0 && 'originalPrice' in formattedPriceDiscounts

  return (
    <PriceWrapper className={className}>
      <PriceHeader>
        {hasDiscount && <Typography $variant="body-s-regular-tight">{formattedPriceDiscounts.originalPrice}</Typography>}
        {hasDiscount && (
          <Tag data-ab-discount-label="true">{`-${formattedPriceDiscounts.discountPercentage}%`}</Tag>
        )}
      </PriceHeader>
      <Typography $variant="display-b-regular" data-qa-price-value={formattedPrice}>
        {formattedPrice}
      </Typography>
      <PriceSupportingText>
        <Typography>
          {intl.formatMessage({ id: 'price_box_price_text' })}
        </Typography>
        <PriceDetails
          priceDetailsQueryParams={{
            transportType,
            accommodationId,
            duration: durationRaw,
            mealPlan,
            departureDate: departureDateRaw,
            participants,
          }}
        />
      </PriceSupportingText>
      {discounts?.description && (
        <PriceSupportingText>
          <Typography $variant="body-s-regular-tight" data-qa-price-discount-description>
            {discounts.description}
          </Typography>
        </PriceSupportingText>
      )}
    </PriceWrapper>
  )
}

export const Price = styled(PriceComponent)``
